import React, { useEffect, useState, useCallback, useRef } from "react";
import { CircularProgress, Button } from "@material-ui/core";
import styled from "styled-components";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import getSystemHealth, { SystemHealth } from "../../../services/SystemHealthService";

const REFRESH_INTERVAL_MS = 10_000;

/* ──────── thresholds → trạng thái ──────── */
type Level = "good" | "warn" | "bad";

const INK = {
	good: "#15803d",
	warn: "#b45309",
	bad: "#b91c1c",
};

function errorLevel(pctTotal: number): Level {
	if (pctTotal < 1) return "good";
	if (pctTotal <= 5) return "warn";
	return "bad";
}
function latencyLevel(p95ms: number): Level {
	if (p95ms < 500) return "good";
	if (p95ms <= 1500) return "warn";
	return "bad";
}
function worst(...levels: Level[]): Level {
	if (levels.includes("bad")) return "bad";
	if (levels.includes("warn")) return "warn";
	return "good";
}

/* ──────── format ──────── */
function formatUptime(seconds: number): string {
	const s = Math.floor(seconds);
	if (s < 60) return `${s} giây`;
	const days = Math.floor(s / 86400);
	const hours = Math.floor((s % 86400) / 3600);
	const minutes = Math.floor((s % 3600) / 60);
	if (days > 0) return `${days} ngày ${hours} giờ`;
	if (hours > 0) return `${hours} giờ ${minutes} phút`;
	return `${minutes} phút`;
}
function formatUpdatedAt(iso: string): string {
	try {
		return new Date(iso).toLocaleString("vi-VN");
	} catch {
		return iso;
	}
}
function num(n: number, digits = 0): string {
	return n.toLocaleString("vi-VN", { maximumFractionDigits: digits });
}

/* ──────── styled (tiết chế: nền trung tính, màu ngữ nghĩa dùng ít) ──────── */
const Page = styled.div`
	max-width: 900px;
	margin: 0 auto;
	background: #ffffff;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	padding: 28px 32px 32px;
`;

const Header = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
	padding-bottom: 20px;
	border-bottom: 1px solid #eef0f3;
`;

const Title = styled.h1`
	font-size: 20px;
	font-weight: 600;
	color: #1f2430;
	margin: 0 0 4px;
	letter-spacing: -0.01em;
`;
const Sub = styled.p`
	font-size: 13px;
	color: #6b7280;
	margin: 0;
`;
const Updated = styled.div`
	font-size: 12px;
	color: #9aa1ac;
	margin-top: 6px;
	font-variant-numeric: tabular-nums;
`;
const RefreshBtn = styled(Button)`
	flex-shrink: 0;
` as typeof Button;

/* dải trạng thái tổng — 1 dòng, nền trung tính, chỉ chấm màu */
const StatusBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	margin: 20px 0 4px;
	padding: 12px 14px;
	background: #f7f8fa;
	border: 1px solid #eef0f3;
	border-radius: 8px;
`;
const StatusLeft = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 9px;
	font-size: 14px;
	font-weight: 600;
	color: #1f2430;
`;
const Dot = styled.span<{ c: string }>`
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background: ${(p) => p.c};
	flex-shrink: 0;
`;
const Uptime = styled.div`
	font-size: 13px;
	color: #6b7280;
	font-variant-numeric: tabular-nums;
`;

/* lưới chỉ số — không thẻ pastel; các ô ngăn bằng đường mảnh */
const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;
const Stat = styled.div`
	padding: 22px 20px;
	border-bottom: 1px solid #eef0f3;
	&:nth-child(odd) {
		border-right: 1px solid #eef0f3;
	}
	@media (max-width: 640px) {
		&:nth-child(odd) {
			border-right: none;
		}
	}
`;
const Label = styled.div`
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #8a919c;
	margin-bottom: 10px;
`;
const Value = styled.div<{ c?: string }>`
	font-size: 30px;
	font-weight: 650;
	line-height: 1;
	color: ${(p) => p.c || "#1f2430"};
	font-variant-numeric: tabular-nums;
`;
const Unit = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: #9aa1ac;
	margin-left: 5px;
`;
const Detail = styled.div`
	font-size: 12.5px;
	color: #6b7280;
	margin-top: 8px;
	line-height: 1.6;
	font-variant-numeric: tabular-nums;
`;

/* thanh đo mảnh (thay cho nền pastel để "liếc là biết") */
const Meter = styled.div`
	height: 5px;
	background: #eef0f3;
	border-radius: 3px;
	margin-top: 12px;
	overflow: hidden;
`;
const MeterFill = styled.div<{ w: number; c: string }>`
	height: 100%;
	width: ${(p) => Math.max(2, Math.min(100, p.w))}%;
	background: ${(p) => p.c};
	border-radius: 3px;
	transition: width 0.4s ease;
`;

const Legend = styled.div`
	font-size: 12px;
	color: #9aa1ac;
	padding: 16px 20px 0;
	line-height: 1.6;
	font-variant-numeric: tabular-nums;
`;
const ErrorBox = styled.div`
	margin: 20px 0 0;
	padding: 14px 16px;
	background: #fdf6f6;
	border: 1px solid #f0d3d3;
	border-radius: 8px;
	color: #8a2a2a;
	font-size: 13.5px;
`;
const Loading = styled.div`
	display: flex;
	justify-content: center;
	padding: 56px 0;
`;

/* ──────── component ──────── */
export default function SystemHealthPage() {
	const [data, setData] = useState<SystemHealth | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mountedRef = useRef(true);

	const fetchData = useCallback(async (showSpinner = false) => {
		if (showSpinner) setRefreshing(true);
		try {
			const result = await getSystemHealth();
			if (!mountedRef.current) return;
			setData(result);
			setError(null);
		} catch (err: any) {
			if (!mountedRef.current) return;
			setError(
				err?.response?.data?.message ||
					err?.message ||
					"Không lấy được số liệu. Vui lòng thử lại.",
			);
		} finally {
			if (mountedRef.current) {
				setLoading(false);
				setRefreshing(false);
			}
		}
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		fetchData();
		intervalRef.current = setInterval(() => fetchData(), REFRESH_INTERVAL_MS);
		return () => {
			mountedRef.current = false;
			if (intervalRef.current !== null) clearInterval(intervalRef.current);
		};
	}, [fetchData]);

	const err = data?.errorRate;
	const lat = data?.latencyMs;
	const eLevel = err ? errorLevel(err.pctTotal) : "good";
	const lLevel = lat ? latencyLevel(lat.p95) : "good";
	const overall = worst(eLevel, lLevel);
	const overallText =
		overall === "good"
			? "Hệ thống bình thường"
			: overall === "warn"
				? "Cần theo dõi"
				: "Có sự cố cần xử lý";

	return (
		<MyBookingWrapper>
			<Page>
				<Header>
					<div>
						<Title>Tình trạng hệ thống</Title>
						<Sub>Số liệu 5 phút gần nhất, tự làm mới mỗi 10 giây.</Sub>
						{data?.at && <Updated>Cập nhật lúc {formatUpdatedAt(data.at)}</Updated>}
					</div>
					<RefreshBtn
						variant="outlined"
						size="small"
						disableElevation
						disabled={loading || refreshing}
						onClick={() => fetchData(true)}>
						{refreshing ? "Đang làm mới…" : "Làm mới"}
					</RefreshBtn>
				</Header>

				{loading && (
					<Loading>
						<CircularProgress size={34} />
					</Loading>
				)}

				{!loading && error && <ErrorBox>Không lấy được số liệu: {error}</ErrorBox>}

				{!loading && !error && data && (
					<>
						<StatusBar>
							<StatusLeft>
								<Dot c={INK[overall]} />
								{overallText}
							</StatusLeft>
							<Uptime>Chạy liên tục {formatUptime(data.uptimeSec)}</Uptime>
						</StatusBar>

						<Grid>
							<Stat>
								<Label>Lượt gọi mỗi giây</Label>
								<Value>
									{num(data.reqPerSec, 2)}
									<Unit>req/s</Unit>
								</Value>
								<Detail>Tổng 5 phút: {num(data.requests)} lượt gọi</Detail>
							</Stat>

							{err && (
								<Stat>
									<Label>Tỉ lệ lỗi</Label>
									<Value c={eLevel === "good" ? undefined : INK[eLevel]}>
										{num(err.pctTotal, 1)}
										<Unit>%</Unit>
									</Value>
									<Meter>
										<MeterFill w={(err.pctTotal / 10) * 100} c={INK[eLevel]} />
									</Meter>
									<Detail>
										Lỗi phía người dùng (4xx): {num(err.pct4xx, 1)}% · Lỗi phía máy chủ
										(5xx): {num(err.pct5xx, 1)}%
									</Detail>
								</Stat>
							)}

							{lat && (
								<Stat>
									<Label>Tốc độ phản hồi (p95)</Label>
									<Value c={lLevel === "good" ? undefined : INK[lLevel]}>
										{num(lat.p95)}
										<Unit>ms</Unit>
									</Value>
									<Meter>
										<MeterFill w={(lat.p95 / 3000) * 100} c={INK[lLevel]} />
									</Meter>
									<Detail>
										95% lượt gọi nhanh hơn mức này. Trung vị {num(lat.p50)} ms · chậm nhất
										{" "}
										{num(lat.max)} ms
									</Detail>
								</Stat>
							)}

							<Stat>
								<Label>Thời gian chạy</Label>
								<Value style={{ fontSize: 24 }}>{formatUptime(data.uptimeSec)}</Value>
								<Detail>Kể từ lần khởi động máy chủ gần nhất</Detail>
							</Stat>

							{data.db && (
								<Stat>
									<Label>Dung lượng cơ sở dữ liệu</Label>
									<Value style={{ fontSize: 26 }}>
										{data.db.error || data.db.pretty == null ? "—" : data.db.pretty}
									</Value>
									<Detail>
										{data.db.error
											? "Chưa lấy được lúc này."
											: "Tổng dung lượng dữ liệu đang lưu. Theo dõi để phát hiện phình bất thường."}
									</Detail>
								</Stat>
							)}
						</Grid>

						<Legend>
							Ngưỡng: tỉ lệ lỗi tốt dưới 1%, theo dõi 1–5%, xử lý trên 5%. Tốc độ p95 tốt
							dưới 500 ms, theo dõi 500–1500 ms, xử lý trên 1500 ms.
						</Legend>
					</>
				)}
			</Page>
		</MyBookingWrapper>
	);
}
